import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import type { YagniCodeDeviceToken } from "@yagni/shared";

import { Button } from "../ui/button.js";
import { Input } from "../ui/input.js";
import { Pane } from "../ui/pane.js";
import { ChapterLede, ChapterSection, ChapterCard } from "./chapter.js";
import { DownloadCodeDialog } from "../shell/DownloadCodeDialog.js";
import { trackClientEvent } from "../analytics/index.js";

/* ═══════════════════════════════════════════════════════════
   YagniCodeDevices

   Settings → You, the YAGNI Code half: the install
   entry, the browser side of the CLI device-authorization flow, and
   (for admins) every connected device with its revoke lever.

   `yagni login` prints a short user code (ABCD-2345) and the
   URL of this page. The logged-in human types that code here; we
   POST it to /api/yagni-code/auth/approve, which binds the pending
   device code to the active workspace + user. The CLI, still polling
   /auth/token, then mints its scoped token and finishes login.

   There is intentionally no "look up the pending request" call — the
   only thing the backend needs from the browser is a session cookie
   plus the user code, so this stays a single form.
   ═══════════════════════════════════════════════════════════ */

export type ApproveDeviceCode = (userCode: string) => Promise<void>;

/**
 * `null` means the caller is not a workspace admin (403): the devices section
 * simply does not render, and the chapter stays the member-facing connect
 * page. The server is the gate; the client only decides what to draw.
 */
export type FetchDeviceTokens = () => Promise<YagniCodeDeviceToken[] | null>;

export type RevokeDeviceToken = (tokenId: string) => Promise<void>;

export interface YagniCodeDevicesProps {
  /** Injected in tests; defaults to the real POST /auth/approve. */
  approveDeviceCode?: ApproveDeviceCode;
  /** Injected in tests; defaults to GET /api/yagni-code-tokens. */
  fetchDeviceTokens?: FetchDeviceTokens;
  /** Injected in tests; defaults to POST /api/yagni-code-tokens/:id/revoke. */
  revokeDeviceToken?: RevokeDeviceToken;
  /** Injected in tests; defaults to window.confirm. */
  confirmRevoke?: (message: string) => boolean;
  /** The install entry, off when the workspace's YAGNI Code flag is off. */
  showInstall?: boolean;
}

export type DeviceTokenStatus = "active" | "expired" | "revoked";

/** Revoked wins over expired wins over active; exported for its unit test. */
export function deviceTokenStatus(token: YagniCodeDeviceToken, now: number): DeviceTokenStatus {
  if (token.revokedAt) return "revoked";
  if (new Date(token.expiresAt).getTime() <= now) return "expired";
  return "active";
}

const defaultFetchDeviceTokens: FetchDeviceTokens = async () => {
  const res = await fetch("/api/yagni-code-tokens", { credentials: "include" });
  if (res.status === 403) return null;
  if (!res.ok) throw new Error(`Failed to load devices (${res.status})`);
  const data = (await res.json()) as { tokens: YagniCodeDeviceToken[] };
  return data.tokens;
};

const defaultRevokeDeviceToken: RevokeDeviceToken = async (tokenId) => {
  const res = await fetch(`/api/yagni-code-tokens/${encodeURIComponent(tokenId)}/revoke`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to revoke the token (${res.status})`);
};

function formatStamp(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Map an /auth/approve failure to a user-facing line. The backend returns
 * two different 404s: `{error:"not_found"}` (the code is wrong/unknown) and a
 * bare 404 with no body when the `yagni_code.enabled` flag is off for the
 * workspace. We tell those apart by the parsed `error` field.
 */
export function messageForApproveFailure(status: number, error?: string): string {
  if (status === 404 && error === "not_found") {
    return "We couldn't find that code. Double-check it and try again.";
  }
  if (status === 404) {
    return "YAGNI Code isn't enabled for this workspace yet. Ask an admin to turn it on.";
  }
  if (status === 409 && error === "already_used") {
    return "That code was already used. Run yagni login again if you still need to connect.";
  }
  if (status === 409) {
    return "That code has expired. Run yagni login again to get a fresh one.";
  }
  if (status === 400) {
    return "Enter the code shown in your terminal.";
  }
  if (status === 401) {
    return "Your session expired. Refresh the page and sign in again.";
  }
  return "Something went wrong approving that code. Try again in a moment.";
}

const defaultApproveDeviceCode: ApproveDeviceCode = async (userCode) => {
  const res = await fetch("/api/yagni-code/auth/approve", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ user_code: userCode }),
  });
  if (res.ok) return;
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  throw new Error(messageForApproveFailure(res.status, body.error));
};

function readPrefill(params: URLSearchParams): string {
  return (params.get("code") ?? params.get("user_code") ?? "").toUpperCase();
}

export function YagniCodeDevices({
  approveDeviceCode = defaultApproveDeviceCode,
  fetchDeviceTokens = defaultFetchDeviceTokens,
  revokeDeviceToken = defaultRevokeDeviceToken,
  confirmRevoke = (message: string) => window.confirm(message),
  showInstall = true,
}: YagniCodeDevicesProps = {}) {
  const [params] = useSearchParams();
  const [installOpen, setInstallOpen] = useState(false);
  const [code, setCode] = useState(() => readPrefill(params));
  const [status, setStatus] = useState<"idle" | "submitting" | "approved">("idle");
  const [error, setError] = useState<string | null>(null);

  // `null` = not shown (member, still loading, or the read failed). Admins get
  // the section only once the list actually arrives.
  const [tokens, setTokens] = useState<YagniCodeDeviceToken[] | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchDeviceTokens()
      .then((loaded) => {
        if (!cancelled) setTokens(loaded);
      })
      .catch(() => {
        // A failed read hides the section rather than blocking the connect
        // form this chapter exists for.
        if (!cancelled) setTokens(null);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchDeviceTokens]);

  async function handleRevoke(token: YagniCodeDeviceToken) {
    const who = token.displayName ?? token.email ?? "a former member";
    if (
      !confirmRevoke(
        `Revoke this device token for ${who}? The CLI using it will stop working immediately.`,
      )
    ) {
      return;
    }
    setRevokingId(token.id);
    setRevokeError(null);
    try {
      await revokeDeviceToken(token.id);
      setTokens((current) =>
        current
          ? current.map((t) =>
              t.id === token.id ? { ...t, revokedAt: new Date().toISOString() } : t,
            )
          : current,
      );
    } catch (err) {
      setRevokeError(err instanceof Error ? err.message : "Failed to revoke the token.");
    } finally {
      setRevokingId(null);
    }
  }

  const trimmed = code.trim();
  const canSubmit = trimmed.length > 0 && status !== "submitting";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (trimmed.length === 0) return;
    setStatus("submitting");
    setError(null);
    try {
      await approveDeviceCode(trimmed);
      setStatus("approved");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Try again.");
      setStatus("idle");
    }
  }

  return (
    <div>
      <ChapterSection label="YAGNI Code" />
      <ChapterLede>
        Install YAGNI Code, then authorize it on this device: run{" "}
        <code className="rounded bg-[var(--bg-code)] px-1 py-0.5 font-mono text-[var(--text-primary)]">
          yagni login
        </code>{" "}
        in your terminal and enter the code it shows you below. This authorizes
        model access, grounded review, next-work suggestions, and writing
        engineering-context comments to YAGNI tickets for this workspace.
      </ChapterLede>
      {showInstall && (
        <div className="mb-4">
          <Button
            variant="secondary"
            data-testid="yagni-code-install"
            onClick={() => {
              trackClientEvent("YAGNI Code Download Opened", { via: "settings" });
              setInstallOpen(true);
            }}
          >
            Get YAGNI Code
          </Button>
          {/* Mounted only while open so the latest-release lookup runs on
              demand, not on every visit to the chapter. */}
          {installOpen && <DownloadCodeDialog open onOpenChange={setInstallOpen} />}
        </div>
      )}
      {status === "approved" ? (
        <ChapterCard className="p-6" data-testid="yagni-code-approved">
          <div className="flex items-start gap-3">
            <CheckCircle2
              size={22}
              aria-hidden
              className="mt-0.5 shrink-0 text-[var(--success)]"
            />
            <div>
              <h3 className="m-0 text-base font-semibold text-[var(--text-primary)]">
                You're connected
              </h3>
              <p className="mt-1 text-sm text-[var(--text-secondary)]">
                Head back to your terminal. YAGNI Code is ready to use.
              </p>
              <Button
                variant="secondary"
                size="sm"
                className="mt-4"
                onClick={() => {
                  setCode("");
                  setError(null);
                  setStatus("idle");
                }}
              >
                Authorize another device
              </Button>
            </div>
          </div>
        </ChapterCard>
      ) : (
        /* Glass: the CLI is polling, waiting on this human to authorize,
           a staged decision. Approval settles it into the printed card above. */
        <Pane material="staged" className="p-6">
        <form onSubmit={handleSubmit} className="space-y-3">
          <label
            htmlFor="yagni-code-input"
            className="block text-sm font-medium text-[var(--text-primary)]"
          >
            Device code
          </label>
          <Input
            id="yagni-code-input"
            value={code}
            onChange={(e) => {
              setCode(e.target.value.toUpperCase());
              if (error) setError(null);
            }}
            placeholder="XXXX-XXXX"
            autoFocus
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            maxLength={12}
            state={error ? "error" : "default"}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? "yagni-code-error" : "yagni-code-help"}
            className="max-w-xs font-mono text-lg uppercase tracking-[0.3em]"
          />
          <p id="yagni-code-help" className="text-xs text-[var(--text-tertiary)]">
            It looks like ABCD-2345. The code expires a few minutes after you start
            the login.
          </p>
          <div className="flex items-center gap-3 pt-1">
            <Button type="submit" disabled={!canSubmit} loading={status === "submitting"}>
              Authorize
            </Button>
            {error ? (
              <span
                id="yagni-code-error"
                role="alert"
                className="text-sm text-[var(--error)]"
              >
                {error}
              </span>
            ) : null}
          </div>
        </form>
        </Pane>
      )}

      {tokens !== null && (
        /* Admin-only (the endpoint 403s members and the section stays hidden):
           every CLI device token in the workspace, and the revoke lever the
           docs promise for a stolen laptop. */
        <>
          <ChapterSection label="Connected devices" />
          {tokens.length === 0 ? (
            <ChapterCard className="p-6">
              <p className="m-0 text-sm text-[var(--text-secondary)]">
                No YAGNI Code devices are connected in this workspace yet.
              </p>
            </ChapterCard>
          ) : (
            <ChapterCard className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-[var(--print-border)] text-left">
                    <th scope="col" className="px-4 py-3 font-semibold text-[var(--text-secondary)]">
                      Developer
                    </th>
                    <th scope="col" className="px-4 py-3 font-semibold text-[var(--text-secondary)]">
                      Created
                    </th>
                    <th scope="col" className="px-4 py-3 font-semibold text-[var(--text-secondary)]">
                      Last used
                    </th>
                    <th scope="col" className="px-4 py-3 font-semibold text-[var(--text-secondary)]">
                      Status
                    </th>
                    <th scope="col" className="px-4 py-3 text-right font-semibold text-[var(--text-secondary)]">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tokens.map((token) => {
                    const state = deviceTokenStatus(token, Date.now());
                    return (
                      <tr
                        key={token.id}
                        className="border-b border-[var(--print-border)] last:border-b-0"
                      >
                        <td className="px-4 py-3">
                          <div className="min-w-0">
                            <div className="font-medium text-[var(--text-primary)]">
                              {token.displayName ?? "Former member"}
                            </div>
                            {token.email && (
                              <div className="text-xs text-[var(--text-tertiary)]">
                                {token.email}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-[family-name:var(--font-mono)] text-[var(--text-secondary)]">
                          {formatStamp(token.createdAt)}
                        </td>
                        <td className="px-4 py-3 font-[family-name:var(--font-mono)] text-[var(--text-secondary)]">
                          {token.lastUsedAt ? formatStamp(token.lastUsedAt) : "Never used"}
                        </td>
                        <td className="px-4 py-3">
                          {state === "revoked" ? (
                            <span className="text-[var(--text-tertiary)]">Revoked</span>
                          ) : state === "expired" ? (
                            <span className="text-[var(--text-tertiary)]">Expired</span>
                          ) : (
                            <span className="text-[var(--text-primary)]">Active</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {state !== "revoked" && (
                            <Button
                              size="sm"
                              variant="secondary"
                              disabled={revokingId === token.id}
                              onClick={() => void handleRevoke(token)}
                            >
                              Revoke
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ChapterCard>
          )}
          {revokeError ? (
            <p role="alert" className="mt-2 text-sm text-[var(--error)]">
              {revokeError}
            </p>
          ) : (
            <p className="mt-2 text-xs text-[var(--text-tertiary)]">
              Revoking a device token signs that CLI out immediately. The
              developer can reconnect any time with yagni login.
            </p>
          )}
        </>
      )}
    </div>
  );
}

